/**
 * Hold-to-talk, implemented as a CLIENT GATE on one hands-free relay session.
 *
 * The button does not start and stop sessions. One stream stays open for the
 * whole conversation, and this decides — frame by frame — whether the
 * microphone's samples or an equal length of silence goes onto the wire. While
 * the gate is closed nothing said in the room can be transcribed, because
 * nothing said in the room is what gets sent.
 *
 * WHY NOT A SESSION PER HOLD. A Muse `PUSH_TO_TALK` stream is one utterance per
 * socket, so every press would cost an upstream handshake and its own ledger
 * row, billed at the 60 s floor (`voiceController.js` `MIN_BILLABLE_SECONDS`).
 * Ten spoken commands would bill ten minutes. `endUtterance()` and
 * `VOICE_MODE_PUSH_TO_TALK` remain for genuine single-shot use.
 *
 * WHY SILENCE RATHER THAN NOTHING. Measured in probe P1.a (2026-09-02, a real
 * Muse stream through the dev relay, `voice-bench/probe-hold-gate.mjs`): one
 * `hands_free` session, an utterance, then six seconds of gate frames, then a
 * second utterance.
 *
 *   - Gate frames = PURE ZERO PCM at realtime: the utterance's `voice:final`
 *     arrived 160 ms of audio after the last speech frame (126 ms wall clock
 *     after release) and its `voice:command` 4 ms later.
 *   - Gate frames = low-level random noise: the same final took 3.1 s. Comfort
 *     noise reads as energy to the endpointer. Noise is out, zeros are in.
 *   - NO frames at all after release: no final ever arrived. Muse's endpointer
 *     advances on AUDIO time, not wall-clock time, so a stream that stops
 *     sending stops the endpointer with it.
 *
 * That last result is the whole reason this is a gate and not an 800 ms tail:
 * a closed gate must keep sending zeros for as long as it is closed. The plan's
 * `tailMs`/`isTailing` interface was measured away by the probe and this module
 * is the simplification that replaced it.
 *
 * The cost is understood and accepted: the server bills forwarded seconds, and
 * closed-gate seconds are forwarded seconds. The 90 s session ceiling and the
 * client's 60 s silence timer still end the session.
 *
 * The same argument later turned out to cover MUTING and READ-BACK HOLDS too —
 * both of which used to drop frames outright — so `outboundFrame` below is the
 * one place that decides what a quantum becomes, for all three reasons at once.
 *
 * Pure — no timers, no clock, no DOM — so the node test can drive it directly.
 */

// 80 ms at 24 kHz: 1,920 samples, 3,840 bytes. This is the send size
// `voiceSession.js` coalesces its render quanta into (`AUDIO_FRAME_SAMPLES`)
// and the size the relay forwards upstream. Exported so the frame arithmetic
// has one home rather than a second copy of the number in a test.
export const FRAME_SAMPLES = 1920;

// The gate is handed render quanta (128 samples), not whole frames, so in
// practice it sees ONE length for an entire session and caches exactly one
// zero array. The cap exists only so a caller that varies its chunk size
// cannot grow the cache without bound; it clears rather than evicting, because
// there is no useful recency order over a handful of buffer lengths.
const ZERO_LENGTH_CACHE_MAX = 8;

// Keyed by sample length, and deliberately MODULE-WIDE rather than per gate:
// every zero frame is read-only (callers only ever copy out of it), so one
// cache serves `createGate().filter` and `outboundFrame` alike and a session
// allocates exactly one array per length instead of one per reason-to-silence.
const zeroFrames = new Map();

/**
 * A zero-filled `Int16Array` of `length`, reused. SHARED and read-only: a
 * caller that writes into it corrupts every other caller's silence.
 */
function zeroFrame(length) {
  const cached = zeroFrames.get(length);
  if (cached) return cached;
  if (zeroFrames.size >= ZERO_LENGTH_CACHE_MAX) zeroFrames.clear();
  const fresh = new Int16Array(length);
  zeroFrames.set(length, fresh);
  return fresh;
}

/**
 * The frame that goes on the wire for one captured quantum.
 *
 * There are three separate reasons the microphone must not be transcribed — the
 * hold-to-talk gate is shut, preview/apply HTTP is in flight (`muted`), or a
 * read-back is playing out of the speaker (`playbackHeld`) — and before this
 * function each of them was expressed as "send nothing". Measured 2026-09-03
 * against the real service: Muse enforces REAL-TIME INGRESS, so a gap of even
 * one second earns `{"type":"error","message":"Ingress audio slower than
 * real-time"}` and a close with code 1008 about 11.5 s later, whether or not
 * frames resume. Sending nothing therefore ends the session; sending zeros of
 * the same length does not, and carries nothing anybody said.
 *
 * So all three reasons collapse into one answer: the same samples when the
 * frame is real, an equal length of silence otherwise.
 *
 * @param {Int16Array} samples one captured quantum
 * @param {object} [state]
 * @param {boolean} [state.gateOpen] the hold-to-talk button is down
 * @param {boolean} [state.muted] preview/apply HTTP is in flight
 * @param {boolean} [state.playbackHeld] a read-back is playing
 * @returns {Int16Array} `samples` itself, or a shared read-only zero frame
 */
export function outboundFrame(samples, { gateOpen, muted, playbackHeld } = {}) {
  // An empty frame rather than the falsy value itself: the caller's next line
  // reads `.length`, and handing back `null` there is a throw dressed up as a
  // guard.
  if (!samples) return zeroFrame(0);
  if (gateOpen && !muted && !playbackHeld) return samples;
  return zeroFrame(samples.length);
}

/**
 * @param {object} [options]
 * @param {boolean} [options.open=false] initial state. Closed by default: a
 *   gate someone forgot to open sends silence, which is safe. A gate someone
 *   forgot to close sends the room.
 * @returns {{open: function, close: function, isOpen: function, filter: function}}
 */
export function createGate({ open = false } = {}) {
  let isOpen = !!open;

  return {
    open() {
      isOpen = true;
    },

    close() {
      isOpen = false;
    },

    isOpen() {
      return isOpen;
    },

    /**
     * The same array when open; a zero-filled array of the SAME LENGTH when
     * closed. The input is never written to, so a caller that keeps its own
     * reference to a captured buffer still holds the real audio.
     *
     * `outboundFrame` has already answered this question by the time
     * `appendAudio` runs, so on the session's path this is now a second, inert
     * pass (zeros in, zeros out). It stays because it is the guarantee the
     * accumulator itself makes, independently of who fills it.
     */
    filter(samples) {
      if (!samples) return zeroFrame(0);
      return isOpen ? samples : zeroFrame(samples.length);
    },
  };
}
